import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignStartVertical,
  BringToFront,
  FlipHorizontal2,
  FlipVertical2,
  Redo2,
  RotateCcw,
  SendToBack,
  Trash2,
  Undo2,
} from 'lucide-react';
import type { ReactElement } from 'react';
import type { FlipAxis, LayerAlignment } from '../model/layer-arrange';
import { ToolbarButton } from './toolbar-button';

interface EditorToolbarProps {
  readonly canReset: boolean;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  /** 레이어를 골랐는지 — 삭제가 이것을 따른다(그룹 포함). */
  readonly hasSelection: boolean;
  /** 고른 레이어가 이미지·텍스트인지 — 그룹은 상자가 없어 정렬할 수 없다. */
  readonly canAlign: boolean;
  /** 고른 레이어가 이미지인지 — 반전은 이미지에만 있다. */
  readonly canFlip: boolean;
  readonly canBringForward: boolean;
  readonly canSendBackward: boolean;
  readonly onReset: () => void;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
  readonly onDelete: () => void;
  readonly onBringForward: () => void;
  readonly onSendBackward: () => void;
  readonly onFlip: (axis: FlipAxis) => void;
  readonly onAlign: (alignment: LayerAlignment) => void;
}

const DIVIDER = 'mx-1 h-5 w-px shrink-0 bg-foreground/15';
const PICK_LAYER = '레이어를 선택하면 쓸 수 있어요';

const ALIGNMENTS: readonly {
  readonly alignment: LayerAlignment;
  readonly icon: typeof AlignStartVertical;
  readonly label: string;
  readonly description: string;
}[] = [
  {
    alignment: 'left',
    icon: AlignStartVertical,
    label: '왼쪽',
    description: '인쇄 영역 왼쪽 정렬',
  },
  {
    alignment: 'center',
    icon: AlignCenterVertical,
    label: '가운데',
    description: '인쇄 영역 가로 가운데 정렬',
  },
  {
    alignment: 'right',
    icon: AlignEndVertical,
    label: '오른쪽',
    description: '인쇄 영역 오른쪽 정렬',
  },
  { alignment: 'top', icon: AlignStartHorizontal, label: '위', description: '인쇄 영역 위쪽 정렬' },
  {
    alignment: 'middle',
    icon: AlignCenterHorizontal,
    label: '중간',
    description: '인쇄 영역 세로 가운데 정렬',
  },
  {
    alignment: 'bottom',
    icon: AlignEndHorizontal,
    label: '아래',
    description: '인쇄 영역 아래쪽 정렬',
  },
];

/**
 * 스테이지 위 가장자리의 도구 줄 — 처음으로 · 취소/다시실행 · 삭제 · 앞으로/뒤로 · 반전 · 정렬.
 * 도구 줄이 넓으면(컨테이너 768px 이상) 아이콘 아래 짧은 이름을 달고, 좁으면 아이콘만 두고 가로로 밀어 본다.
 * 막힌 버튼은 툴팁으로 이유를 알린다. 편집은 목업 위에서 바로 반영되므로 [완료] 단계가 없다.
 * 그룹 만들기·풀기는 공식 에디터 범위 밖이라 두지 않는다(스펙 §1 비범위).
 */
export const EditorToolbar = ({
  canReset,
  canUndo,
  canRedo,
  hasSelection,
  canAlign,
  canFlip,
  canBringForward,
  canSendBackward,
  onReset,
  onUndo,
  onRedo,
  onDelete,
  onBringForward,
  onSendBackward,
  onFlip,
  onAlign,
}: EditorToolbarProps): ReactElement => {
  const flipHint = hasSelection ? '이미지에만 쓸 수 있어요' : '이미지를 선택하면 쓸 수 있어요';
  const alignHint = hasSelection ? '그룹은 정렬할 수 없어요' : PICK_LAYER;
  return (
    <div className="@container shrink-0 border-b border-foreground/10 bg-background">
      <div
        role="toolbar"
        aria-label="편집 도구"
        className="flex h-12 items-center overflow-x-auto px-2 [scrollbar-width:none] sm:px-3 @3xl:h-16 @3xl:justify-center"
      >
        <ToolbarButton
          icon={RotateCcw}
          label="처음으로"
          description="처음으로 — 모든 면의 디자인 지우기"
          disabled={!canReset}
          disabledHint="지울 디자인이 없어요"
          onClick={onReset}
        />
        <span aria-hidden className={DIVIDER} />
        <ToolbarButton
          icon={Undo2}
          label="취소"
          description="취소 (⌘/Ctrl+Z)"
          disabled={!canUndo}
          disabledHint="되돌릴 작업이 없어요"
          onClick={onUndo}
        />
        <ToolbarButton
          icon={Redo2}
          label="다시실행"
          description="다시실행 (⌘/Ctrl+Shift+Z)"
          disabled={!canRedo}
          disabledHint="다시 할 작업이 없어요"
          onClick={onRedo}
        />
        <span aria-hidden className={DIVIDER} />
        <ToolbarButton
          icon={Trash2}
          label="삭제"
          description="선택한 레이어 삭제"
          disabled={!hasSelection}
          disabledHint={PICK_LAYER}
          onClick={onDelete}
        />
        <span aria-hidden className={DIVIDER} />
        <ToolbarButton
          icon={BringToFront}
          label="앞으로"
          description="한 단계 앞으로"
          disabled={!canBringForward}
          disabledHint={hasSelection ? '이미 맨 앞이에요' : PICK_LAYER}
          onClick={onBringForward}
        />
        <ToolbarButton
          icon={SendToBack}
          label="뒤로"
          description="한 단계 뒤로"
          disabled={!canSendBackward}
          disabledHint={hasSelection ? '이미 맨 뒤예요' : PICK_LAYER}
          onClick={onSendBackward}
        />
        <span aria-hidden className={DIVIDER} />
        <ToolbarButton
          icon={FlipHorizontal2}
          label="좌우반전"
          description="좌우반전 (이미지)"
          disabled={!canFlip}
          disabledHint={flipHint}
          onClick={() => onFlip('x')}
        />
        <ToolbarButton
          icon={FlipVertical2}
          label="상하반전"
          description="상하반전 (이미지)"
          disabled={!canFlip}
          disabledHint={flipHint}
          onClick={() => onFlip('y')}
        />
        <span aria-hidden className={DIVIDER} />
        {ALIGNMENTS.map((entry) => (
          <ToolbarButton
            key={entry.alignment}
            icon={entry.icon}
            label={entry.label}
            description={entry.description}
            disabled={!canAlign}
            disabledHint={alignHint}
            onClick={() => onAlign(entry.alignment)}
          />
        ))}
      </div>
    </div>
  );
};
